/**
 * Multi-device call-claim liveness model (2026-08 "false 2-devices" incident).
 *
 * A device that has live call media publishes a DEVICE_CLAIM for the call. The
 * claim is stored ON the call's SESSION# row, but — critically — it is stored
 * PER USER (`deviceClaim:<cognito-sub>`): a 1:1 call has TWO legitimate media
 * owners (caller + callee), one per user. The previous schema kept a single
 * shared `activeDeviceId` field, so the second party to connect "lost" the
 * claim to the FIRST PARTY'S device and was told "Call is active on another
 * device" — tearing down its own live media and showing the false switch
 * prompt (what Ania saw with no second session anywhere).
 *
 * Liveness rules (product mandate):
 *  1. A claim is only believable while it heartbeats: `at` must be renewed on
 *     the heartbeat cadence, and any consumer (the switch prompt, rehydrate's
 *     "active elsewhere" state, soft-claim arbitration) must require freshness
 *     within {@link DEVICE_CLAIM_FRESH_MS} — row existence alone proves nothing.
 *  2. Graceful release on exit ('release' action) removes the claim; a missed
 *     release (crash / kill) is covered by the freshness window expiring.
 *  3. Malformed / missing claim data must degrade to "no prompt", never to a
 *     teardown ({@link parseDeviceClaim} is deliberately forgiving).
 */

export type DeviceClaimPlatform = 'ios' | 'android' | 'web'

/**
 * The claiming device ends a native-media 1:1 call by itself when the peer
 * leaves the meeting while its WebView is suspended (iOS remote-left
 * backstop). Only then may the backend skip the remote-ended VoIP push to it.
 */
export const REMOTE_LEFT_BACKSTOP_CAP = 'remote_left_backstop_v1'

export type DeviceClaimCap = typeof REMOTE_LEFT_BACKSTOP_CAP

const KNOWN_DEVICE_CLAIM_CAPS: ReadonlySet<string> = new Set<DeviceClaimCap>([
  REMOTE_LEFT_BACKSTOP_CAP,
])

export type DeviceClaim = {
  deviceId: string
  platform: DeviceClaimPlatform
  /** ISO timestamp of the last claim write OR heartbeat renewal. */
  at: string
  /** Capabilities the claiming device advertised (absent on older clients). */
  caps?: DeviceClaimCap[]
}

/** Known caps from an untrusted value; anything else is dropped. */
export function parseDeviceClaimCaps(value: unknown): DeviceClaimCap[] {
  if (!Array.isArray(value)) return []
  const caps = value.filter(
    (cap): cap is DeviceClaimCap => typeof cap === 'string' && KNOWN_DEVICE_CLAIM_CAPS.has(cap),
  )
  return Array.from(new Set(caps))
}

/**
 * Caps a client advertises on its device claim. The remote-left backstop only
 * exists on iOS for calls whose audio runs through native Chime media.
 */
export function advertisedDeviceClaimCaps(input: {
  platform: DeviceClaimPlatform
  nativeMediaCall: boolean
}): DeviceClaimCap[] {
  return input.platform === 'ios' && input.nativeMediaCall ? [REMOTE_LEFT_BACKSTOP_CAP] : []
}

/** Client heartbeat cadence (mirrors HEARTBEAT_INTERVAL_MS in the controller). */
export const DEVICE_CLAIM_HEARTBEAT_MS = 20_000

/**
 * How stale a claim may be and still count as "live". Two missed heartbeats
 * plus network slack — tight enough that a closed tab stops prompting within
 * ~45s, loose enough that one dropped heartbeat doesn't flap ownership.
 */
export const DEVICE_CLAIM_FRESH_MS = 45_000

/** DynamoDB attribute holding this user's claim on the SESSION# row. */
export function claimAttributeNameForUser(userId: string): string {
  return `deviceClaim:${userId}`
}

/**
 * Parse an untrusted claim value from DynamoDB / a signal payload. Returns
 * null for anything malformed — per the mandate, bad claim data must never do
 * worse than "no prompt". A missing / unknown platform reads as 'web' here;
 * decisions where that default is unsafe use {@link explicitDeviceClaimPlatform}.
 */
export function parseDeviceClaim(value: unknown): DeviceClaim | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const deviceId = typeof record.deviceId === 'string' ? record.deviceId.trim() : ''
  const at = typeof record.at === 'string' ? record.at : ''
  const platform = explicitDeviceClaimPlatform(record) ?? 'web'
  if (!deviceId || !at || Number.isNaN(Date.parse(at))) return null
  const caps = parseDeviceClaimCaps(record.caps)
  return caps.length > 0 ? { deviceId, platform, at, caps } : { deviceId, platform, at }
}

/** The claim's platform as written, or null when missing / unrecognised. */
export function explicitDeviceClaimPlatform(value: unknown): DeviceClaimPlatform | null {
  if (!value || typeof value !== 'object') return null
  const platform = (value as Record<string, unknown>).platform
  return platform === 'ios' || platform === 'android' || platform === 'web' ? platform : null
}

/** True while the claim's renewed-at timestamp is within the freshness window. */
export function isDeviceClaimFresh(
  claim: DeviceClaim | null,
  nowMs: number = Date.now(),
  freshMs: number = DEVICE_CLAIM_FRESH_MS,
): boolean {
  if (!claim) return false
  const at = Date.parse(claim.at)
  if (!Number.isFinite(at)) return false
  // Future-dated claims (clock skew) are trusted only within the same window.
  return Math.abs(nowMs - at) < freshMs
}

/**
 * Should THIS device treat the call as live on another of the SAME USER's
 * devices? Requires a well-formed, FRESH claim naming a different device.
 * Anything else (missing row, stale claim, malformed data, our own device)
 * → false → no prompt, no teardown.
 */
export function claimIndicatesActiveElsewhere(
  claimValue: unknown,
  thisDeviceId: string | null | undefined,
  nowMs: number = Date.now(),
): claimValue is DeviceClaim {
  const claim = parseDeviceClaim(claimValue)
  if (!claim || !isDeviceClaimFresh(claim, nowMs)) return false
  if (!thisDeviceId) return false
  return claim.deviceId !== thisDeviceId
}
